import { useUpdateUser } from '@reflag/react-sdk';
import type * as React from 'react';
import { useSetRecoilState } from 'recoil';

import { Alert, confirm, toast } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { appReloadingMessageAtom } from '../../Application';
import { CustomerIOActionHandler } from '../../Components';
import { useNavigation } from '../../navigation';
import { siteCentricOptInAtom } from '../../state';
import { siteCentricNavConfirmDialogImage } from './SiteCentricNavConfirmDialogAssets';
import { transitionSiteCentric } from './SiteCentricTransition';

/**
 * Trigger the site-centric navigation confirmation dialog from a Customer.io action.
 */
export function SiteCentricNavConfirmDialog(props: {
    organizationId: string;
    isSiteCentricNavEnabled: boolean;
}): React.ReactElement | null {
    const updateUser = useUpdateUser();
    const { getNavigationState, navigate } = useNavigation();
    const setAppReloadingMessage = useSetRecoilState(appReloadingMessageAtom);
    const setSiteCentricOptIn = useSetRecoilState(siteCentricOptInAtom);

    const enableSiteCentricNav = useEventCallback(async (onReject?: () => void) => {
        if (props.isSiteCentricNavEnabled) {
            return;
        }

        await toast.catch(
            async () => {
                await transitionSiteCentric({
                    landingScreen: getNavigationState().screen,
                    navigate,
                    organizationId: props.organizationId,
                    reloadingMessage: 'Getting your new experience ready',
                    setAppReloadingMessage,
                    setSiteCentricOptIn,
                    siteCentricOptIn: 'true',
                    updateUser,
                });
            },
            'Error while enabling the new site-centric navigation',
            { onReject }
        );
    });

    const openDialog = useEventCallback(async () => {
        void confirm({
            compact: true,
            title: 'The new site workspace is here',
            image: {
                src: siteCentricNavConfirmDialogImage,
                resize: false,
                width: '100%',
                height: 'auto',
                alt: 'Preview of a structured documentation sidebar',
                style: { aspectRatio: '831 / 437' },
            },
            description: (
                <>
                    <p
                        ref={(element) => {
                            element?.focus({ preventScroll: true });
                        }}
                        tabIndex={-1}
                    >
                        GitBook is now organized around your sites. Open a site and find its
                        content, change requests, customization, insights, and settings all in one
                        place.
                    </p>

                    <Alert
                        kind="default"
                        size="small"
                        className="mt-4"
                        details="You can switch back at any time from your organization menu."
                    />
                </>
            ),
            confirmLabel: props.isSiteCentricNavEnabled ? 'Got it' : "Let's go!",
            onConfirm: async (_event, { preventClose }) => {
                await enableSiteCentricNav(preventClose);
            },
            onCancel: () => {
                void enableSiteCentricNav();
            },
            cancelLabel: null,
        });
    });

    return (
        <CustomerIOActionHandler actionName="show-site-centric-nav-dialog" onAction={openDialog} />
    );
}
